import { Component } from '@theme/component';
import {
  fetchConfig,
  debounce,
  onAnimationEnd,
  prefersReducedMotion,
  resetShimmer,
  startViewTransition,
} from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);

  /**
   * Finds the cart item row for a given cart line number.
   *
   * Rows are matched by their `data-line` attribute rather than DOM position,
   * because the rendered order can differ from cart-line order (e.g. the
   * donation line is rendered last) and the donation line has no quantity
   * selector — so positional indexing would desync.
   *
   * @param {number} line - The 1-based cart line number.
   * @returns {HTMLElement | null} The matching row, or null.
   */
  #rowForLine(line) {
    return this.refs.cartItemRows?.find((row) => Number(row.dataset.line) === line) ?? null;
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity, cartLine: line } = event.detail;

    // Cart items require a line number
    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
    const lineItemRow = this.#rowForLine(line);

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });

    const cartItemRowToRemove = this.#rowForLine(line);

    if (!cartItemRowToRemove) return;

    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

    // If the cart item row is the last row, optimistically trigger the cart empty state
    const isEmptyCart = rowsToRemove.length == this.refs.cartItemRows.length;

    const template = document.getElementById('empty-cart-template');
    if (isEmptyCart && template instanceof HTMLTemplateElement) {
      const clone = document.importNode(template.content, true);

      startViewTransition(() => {
        this.replaceChildren(clone);
      }, [this.isDrawer ? 'empty-cart-drawer' : 'empty-cart-page']);

      return;
    }

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    });
  }

  /**
   * Handles switching a line item to a different variant via the in-cart picker.
   * Shopify has no atomic "swap variant" endpoint, so we add the new variant and
   * then remove the original line, re-rendering every cart surface from the change.
   *
   * @param {number} line - The 1-based line index of the item being switched.
   * @param {Event} event - The change event from the variant `<select>`.
   */
  async onVariantSwitch(line, event) {
    const select = event.target;
    if (!(select instanceof HTMLSelectElement)) return;

    const newVariantId = select.value;
    const currentVariantId = select.dataset.currentVariantId;
    if (!newVariantId || newVariantId === currentVariantId) return;

    const quantity = parseInt(select.dataset.quantity ?? '1', 10) || 1;

    /** @type {Record<string, unknown>} */
    const addItem = { id: Number(newVariantId), quantity };

    if (select.dataset.sellingPlan) addItem.selling_plan = Number(select.dataset.sellingPlan);
    if (select.dataset.properties) {
      try {
        addItem.properties = JSON.parse(select.dataset.properties);
      } catch (error) {
        console.error(error);
      }
    }

    this.#disableCartItems();

    const lineItemRow = this.refs.cartItemRows[line - 1];
    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow?.querySelector('text-component'));
    textComponent?.shimmer();
    this.refs.cartTotal?.shimmer();

    try {
      const response = await fetch(
        `${Theme.routes.cart_add_url}`,
        fetchConfig('json', { body: JSON.stringify({ items: [addItem] }) })
      );
      const data = await response.json();

      if (!response.ok || data.status) {
        // Add failed (e.g. the target variant sold out between render and click).
        // Leave the original line untouched, revert the control, and surface the error.
        resetShimmer(this);
        this.#enableCartItems();
        if (currentVariantId) select.value = currentVariantId;
        this.#showVariantSwitchError(line, data.description || data.message);
        return;
      }
    } catch (error) {
      console.error(error);
      resetShimmer(this);
      this.#enableCartItems();
      if (currentVariantId) select.value = currentVariantId;
      return;
    }

    // Remove the original line; updateQuantity re-renders and re-enables every cart section.
    this.updateQuantity({ line, quantity: 0, action: 'change' });
  }

  /**
   * Surfaces a variant-switch error in the line's existing error region.
   * @param {number} line - The 1-based line index.
   * @param {string} [message] - The error message to display.
   */
  #showVariantSwitchError(line, message) {
    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (cartItemError instanceof HTMLElement && cartItemErrorContainer instanceof HTMLElement) {
      cartItemError.textContent = message ?? '';
      cartItemErrorContainer.classList.remove('hidden');
    }
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      line: line,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => {
        return response.text();
      })
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText);
          window.publishStorefrontEvent?.('cart_update_failed', {
            component: 'cart_items',
            operation: quantity === 0 ? 'remove' : 'quantity_change',
            error_type: 'server_rejected',
            placement: this.isDrawer ? 'cart_drawer' : 'cart_page',
          });
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        // Update data-cart-quantity for all matching variants
        this.#updateQuantitySelectors(parsedResponseText);

        this.dispatchEvent(
          new CartUpdateEvent(parsedResponseText, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
          })
        );

        morphSection(this.sectionId, parsedResponseText.sections[this.sectionId], this.isDrawer ? 'hydration' : 'full');

        this.#updateCartQuantitySelectorButtonStates();
      })
      .catch((error) => {
        console.error(error);
        window.publishStorefrontEvent?.('cart_update_failed', {
          component: 'cart_items',
          operation: quantity === 0 ? 'remove' : 'quantity_change',
          error_type: 'network',
          placement: this.isDrawer ? 'cart_drawer' : 'cart_page',
        });
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const row = this.#rowForLine(line);
    const quantityInput = row?.querySelector('input[data-cart-line]');

    // Donation rows have no quantity input; skip the value reset but still surface the error.
    if (quantityInput instanceof HTMLInputElement) {
      quantityInput.value = quantityInput.defaultValue;
    }

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      const morph = () => {
        morphSection(this.sectionId, cartItemsHtml);

        // Update button states for all cart quantity selectors after morph
        this.#updateCartQuantitySelectorButtonStates();
      };

      // Empty -> active: play the inverse of the active -> empty view transition
      // (the empty state shrinks/fades out, the items grow into place) instead of
      // jumping. Other updates (quantity changes on a non-empty cart) just morph.
      if (this.#isShowingEmptyState()) {
        startViewTransition(morph, [this.isDrawer ? 'active-cart-drawer' : 'active-cart-page']);
      } else {
        morph();
      }
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }
  };

  /**
   * @returns {boolean} Whether the component is currently rendering its empty state.
   */
  #isShowingEmptyState() {
    return Boolean(this.querySelector('.cart-drawer__items--empty, .cart-page--empty'));
  }

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }

  /**
   * @returns {boolean} Whether the component is a drawer.
   */
  get isDrawer() {
    return this.dataset.drawer !== undefined;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
